/* eslint-disable @typescript-eslint/no-explicit-any */
import { type Trello } from "../types/trello"
import { BoardMode, getStoredCardCustomer, getStoredTrackedTime } from "../storage"
import { qboCustomerUrl } from "../card-back-section/helpers/helpers"
import { formatMinutes } from "../card-back-section/helpers/time-format"

// Badge callbacks run once per visible card on the board, so they read the
// denormalized copy in Trello's shared card data rather than calling the API.
// See storage.ts for how that copy is kept in step with CardCustomerLink.

/**
 * The mirrored customer, or null when it can't be trusted for this card.
 *
 * Copying a card duplicates its shared data but not the API-side link, so the
 * copy inherits the original's customer. Rejecting a copy whose stamped card id
 * doesn't match the card in context keeps the badge off it until the card back
 * reconciles. Copies written before the stamp existed carry no card id and are
 * accepted — the card back restamps them on the next open.
 */
const badgeCustomer = async (t: Trello.PowerUp.IFrame) => {
  const customer = await getStoredCardCustomer(t)
  if (!customer?.name) return null

  const contextCardId = t.getContext().card
  if (customer.cardId && contextCardId && customer.cardId !== contextCardId) return null

  return customer
}

/**
 * The card's tracked time, or null when there is none to show.
 *
 * Same copy-guard as the customer: a duplicated card inherits the shared data
 * but not the entries, which are keyed by card id. Mirrors written before the
 * badge existed carry no minutes, and stay off the board until the card back
 * next reconciles them.
 */
const badgeTrackedTime = async (t: Trello.PowerUp.IFrame) => {
  const tracked = await getStoredTrackedTime(t)
  if (!tracked?.minutes) return null

  const contextCardId = t.getContext().card
  if (tracked.cardId && contextCardId && tracked.cardId !== contextCardId) return null

  return tracked.minutes
}

export async function getCardBadges(
  t: Trello.PowerUp.IFrame,
  props: any,
  mode: BoardMode
): Promise<Trello.PowerUp.CardBadge[]> {
  if (mode === "time") {
    const minutes = await badgeTrackedTime(t)
    return minutes
      ? [{ icon: props.baseUrl + props.icon.light, text: formatMinutes(minutes) }]
      : []
  }

  const customer = await badgeCustomer(t)
  if (!customer) return []

  return [
    {
      icon: props.baseUrl + props.icon.light,
      text: customer.name,
    },
  ]
}

export async function getCardDetailBadges(
  t: Trello.PowerUp.IFrame,
  props: any,
  mode: BoardMode
): Promise<Trello.PowerUp.CardDetailBadge[]> {
  if (mode === "time") {
    const minutes = await badgeTrackedTime(t)
    return minutes
      ? [
          {
            title: "Tracked time",
            icon: props.baseUrl + props.icon.light,
            text: formatMinutes(minutes),
          },
        ]
      : []
  }

  const customer = await badgeCustomer(t)
  if (!customer) return []

  return [
    {
      title: "Customer",
      icon: props.baseUrl + props.icon.light,
      text: customer.name,
      url: qboCustomerUrl(customer.id),
      target: "QuickBooks",
    },
  ]
}
